/**
 * Putting a panel in its own OS window, and the four facts that decide whether
 * it works.
 *
 * The DOM half is here rather than in the component for this app's usual reason:
 * a component that reaches a chart cannot be tested, and none of this needs one.
 * jsdom has `document`, so everything below is exercised for real.
 *
 * ## Opening must happen inside the click
 *
 * Browsers allow `window.open` only from a user gesture. An effect that opens
 * one after a state change is *usually* still inside the gesture's task and is
 * blocked the moment it is not — a failure that appears on somebody else's
 * machine, in a build with a slightly different scheduler. {@link openDataWindow}
 * is therefore called straight from the handler and the reference is handed to
 * the component, rather than the component opening it on mount.
 *
 * ## The tokens have to be written onto the popup's own document
 *
 * Every component in this app carries inline styles and reads `var(--qf-*)`, so
 * there is **no stylesheet to clone** — which is the one thing that makes this
 * cheap. What a second document does not inherit is the variables themselves,
 * and its own `body`, which is white until something says otherwise.
 *
 * ## One window, named
 *
 * `window.open` reuses a window of the same name, so a second detach focuses the
 * one already open rather than leaving two panels claiming to be the panel.
 *
 * ## A refusal can only be learned by asking
 *
 * Some browsers decline `window.open` from script outright — DuckDuckGo returns
 * `null` for a bare `open("", "name")`, with no features involved and no blocker
 * setting to toggle. That cannot be detected in advance: a probe at load has no
 * user activation, so it would answer `null` in *every* browser and hide the
 * control everywhere. {@link canDetach} answers what a media query can see, and
 * no more.
 *
 * So the first click is the test. A refusal seen from a real gesture is reliable
 * evidence, and the caller records it for the session — the control then says
 * {@link DETACH_REFUSED_LABEL} and stops offering what it cannot do. Coming home
 * in silence is what made this read as a dead button.
 */

import type { Theme } from "@qufolio/model";
import { themeCssVariables } from "@qufolio/model";

/**
 * The window's name, which is what makes a second open reuse the first.
 *
 * One per app rather than one per chart: there is one data panel, so there is
 * one window for it to become.
 */
export const DATA_WINDOW_NAME = "qf-data";

export const DATA_WINDOW_SIZE = { width: 460, height: 620 };

/**
 * One view of the panel in a window of its own — Fin St on one monitor, News on
 * another (owner, 2026-10-28; docs/plans/desktop-electron.md rule 3).
 *
 * Named per view, so detaching a view twice focuses the one window, and so the
 * desktop shell, which remembers pop-outs by name, puts each back where it was.
 */
export const VIEW_WINDOW_PREFIX = `${DATA_WINDOW_NAME}:`;

export function viewWindowName(view: string): string {
  return `${VIEW_WINDOW_PREFIX}${view}`;
}

/** The views among a list of window names — what the shell says was open at quit. */
export function viewsInWindows<V extends string>(names: readonly string[], known: readonly V[]): V[] {
  return names
    .filter((n) => n.startsWith(VIEW_WINDOW_PREFIX))
    .map((n) => n.slice(VIEW_WINDOW_PREFIX.length) as V)
    .filter((v, i, all) => known.includes(v) && all.indexOf(v) === i);
}

/**
 * Which view the panel itself shows: the one asked for, unless it is out in its
 * own window — then the first that is not. A view is in one place at a time, as
 * the panel is. `undefined` when every view is out.
 */
export function shownView<T extends { id: string }>(
  views: readonly T[],
  tab: string,
  detached: readonly string[],
): T | undefined {
  const home = views.filter((v) => !detached.includes(v.id));
  return home.find((v) => v.id === tab) ?? home[0];
}

/**
 * What the control says once this browser has refused.
 *
 * Shown only after a refusal has actually been seen, never predicted — see the
 * module header. It names the browser as the cause because every other reading
 * ("the button is broken", "the panel is gone") sends the reader somewhere the
 * fault is not.
 */
export const DETACH_REFUSED_LABEL = "Your browser blocked the separate window";

/**
 * The feature string, which has to say `popup` to get a window rather than a tab.
 *
 * `width` and `height` alone are enough on Chromium and are ignored by Firefox
 * without it, so a browser that opened a tab would portal the panel into
 * something the user cannot put on a second monitor — the entire point.
 */
export function popupFeatures({ width, height }: { width: number; height: number }): string {
  return `popup=yes,width=${Math.round(width)},height=${Math.round(height)}`;
}

/**
 * Whether the detach control should be offered at all.
 *
 * Pure, because both inputs are media queries the caller reads and neither is
 * worth a DOM in a test.
 *
 * **Not on a phone**, where there is no second monitor and a popup is a tab.
 * **Not in an installed PWA**: `mobile.md` records that a popup opened from
 * standalone display mode can come up detached and lose its callback, which is
 * the same failure `signInWithPopup` has there. A control that sometimes strands
 * the thing it opened is worse than one that is not shown.
 */
export function canDetach(narrow: boolean, standalone: boolean): boolean {
  return !narrow && !standalone;
}

/** Whether the app is running as an installed PWA. */
export function isStandalone(): boolean {
  return (
    globalThis.matchMedia?.("(display-mode: standalone)").matches ??
    // iOS Safari answers the question on `navigator` and not through a media
    // query, and it is the platform the popup caveat was recorded against.
    (globalThis.navigator as { standalone?: boolean } | undefined)?.standalone ??
    false
  );
}

/**
 * Open (or focus) the data window.
 *
 * Returns `null` when the browser refused, which is a real outcome rather than
 * an error: a blocker denies silently, and the caller has to stay docked instead
 * of rendering a portal into nothing.
 */
export function openDataWindow(
  open: typeof window.open = window.open.bind(window),
  name: string = DATA_WINDOW_NAME,
): Window | null {
  const win = open("", name, popupFeatures(DATA_WINDOW_SIZE));
  // Focus, so a second detach on an already-open window brings it forward
  // instead of doing nothing visible. Guarded: a cross-origin or closed window
  // throws here, and failing to raise a window is not worth an exception.
  try {
    win?.focus();
  } catch {
    /* not raising the window is not a failure worth reporting */
  }
  return win ?? null;
}

/**
 * Paint the popup's document with the app's theme.
 *
 * The variables go on `documentElement` rather than on a wrapper, so anything
 * portalled in resolves `var(--qf-*)` exactly as it does in the opener — the
 * components are shared and must not know which document they are in.
 *
 * The body is painted explicitly because a second document does not inherit one:
 * without it the panel's own surfaces are drawn on white, which reads as a
 * rendering fault rather than as a default.
 *
 * Re-applied whenever the theme changes, or a detached panel keeps the palette
 * it was born with.
 */
export function applyDocumentTheme(doc: Document, theme: Theme): void {
  const root = doc.documentElement;
  for (const [name, value] of Object.entries(themeCssVariables(theme))) {
    root.style.setProperty(name, value);
  }
  const body = doc.body;
  body.style.margin = "0";
  body.style.background = "var(--qf-background)";
  body.style.color = "var(--qf-text)";
  body.style.font = "13px system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
  // The panel fills the window rather than sitting in the corner of it: a
  // detached panel *is* the window, and a 440px card floating in a 460px frame
  // reads as a mistake.
  body.style.height = "100vh";
  body.style.overflow = "hidden";
}

/** Give the popup a title, so the taskbar entry names what is in it. */
export function setDocumentTitle(doc: Document, title: string): void {
  doc.title = title;
}
